import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isMockMode } from "./mock/mode";
import { baseUrl } from "./services";
import { freshToken } from "../auth/refresh";

/**
 * Jonli yangilanishlar (SSE).
 *
 * Ilgari har bir ro'yxat `refetchInterval` bilan qayta so'ralardi:
 * hech narsa o'zgarmagan bo'lsa ham so'rov ketardi, o'zgargan
 * bo'lsa esa yarim daqiqagacha kutilardi. Endi server proyeksiya
 * o'zgarganda xabar beradi va kesh AYNAN O'SHA daqiqada bekor
 * qilinadi.
 *
 * `EventSource` ISHLATILMAYDI: u so'rovga sarlavha qo'sha olmaydi
 * va token URL ichiga tushib, server jurnaliga va brauzer
 * tarixiga yozilib qolardi. Shuning uchun oqim `fetch` bilan
 * o'qiladi — `Authorization` sarlavhasi odatdagidek ketadi.
 */

interface StreamEvent {
  projection: string;
  event_type: string;
  at: string;
}

/**
 * Qaysi proyeksiya qaysi so'rovlarni eskirtiradi.
 *
 * Hammasini birdan bekor qilish ham mumkin edi, lekin bitta
 * harakat hodisasi butun interfeysni qayta yuklardi. Xarita
 * esa TOPOLOGIYA va QOLDIQ ikkalasiga ham bog'liq: katak
 * o'rni topologiyadan, rangi qoldiqdan keladi.
 */
const AFFECTED: Record<string, string[][]> = {
  topology: [["report"], ["map"], ["topology"]],
  inventory: [["report"], ["map"], ["inventory"]],
  task: [["report"], ["task"]],
  control: [["report"]],
  picking: [["report"]],
  putaway: [["report"]],
  replenishment: [["report"]],
  "inventory-count": [["report"], ["count"]],
  master: [["report"], ["masterData"]],
  reporting: [["report"], ["map"]],
};

export type StreamStatus = "connecting" | "live" | "offline" | "mock";

/**
 * Oqimga ulanadi va o'zgarish kelganda keshni bekor qiladi.
 *
 * Uzilganda qayta ulanadi, lekin darhol emas: servis qayta
 * ishga tushayotgan bo'lsa, bir necha ochiq oyna uni bir
 * vaqtda qayta so'rov bilan bosardi.
 */
export function useLiveUpdates(): StreamStatus {
  const client = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>("connecting");

  useEffect(() => {
    // Namuna rejimida oqim YO'Q va uni "jonli" deb ko'rsatish
    // yolg'on bo'lardi: alohida holat qaytariladi va
    // ko'rsatkich buni ochiq aytadi.
    if (isMockMode()) {
      setStatus("mock");
      return;
    }

    const controller = new AbortController();
    let retry: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let attempt = 0;

    const connect = async () => {
      const token = await freshToken();
      if (!token) return;

      try {
        const response = await fetch(`${baseUrl("reporting")}/api/v1/stream`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

        setStatus("live");
        attempt = 0;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE xabarlari BO'SH QATOR bilan ajraladi va bitta
          // o'qishda yarim xabar kelishi mumkin — shuning uchun
          // to'liq bloklargina ajratiladi.
          let boundary = buffer.indexOf("\n\n");
          while (boundary !== -1) {
            handleBlock(buffer.slice(0, boundary), client);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");
          }
        }
      } catch {
        /* uzilish — pastda qayta urinish rejalashtiriladi */
      }

      if (stopped) return;
      setStatus("offline");

      // Ikki soniyadan boshlab, o'ttiz soniyagacha o'sadi.
      attempt += 1;
      const delay = Math.min(2000 * 2 ** (attempt - 1), 30_000);
      retry = setTimeout(() => void connect(), delay);
    };

    void connect();

    return () => {
      stopped = true;
      controller.abort();
      if (retry) clearTimeout(retry);
    };
  }, [client]);

  return status;
}

function handleBlock(block: string, client: ReturnType<typeof useQueryClient>): void {
  // Izoh qatori (`: ping`) — ulanishni ochiq ushlab turish uchun.
  if (!block.startsWith("event:")) return;

  const dataLine = block.split("\n").find((line) => line.startsWith("data:"));
  if (!dataLine) return;

  let event: StreamEvent;
  try {
    event = JSON.parse(dataLine.slice(5).trim()) as StreamEvent;
  } catch {
    return;
  }

  const keys = AFFECTED[event.projection];
  if (!keys) {
    // Noma'lum domen — yangi event turi qo'shilgan bo'lishi
    // mumkin. Hisobotlar baribir yangilanadi: eskirgan ro'yxat
    // ko'rsatilgandan ko'ra ortiqcha so'rov yaxshiroq.
    void client.invalidateQueries({ queryKey: ["report"] });
    return;
  }
  for (const key of keys) {
    void client.invalidateQueries({ queryKey: key });
  }
}
